import * as server from "../resources/constants/api";

const DEFAULT_CHAT_BACKEND_PATH = "/api/chat/event-suggestions";
const DEFAULT_TIMEOUT_MS = 30000;

const normalizeRole = (role) => (role === "assistant" ? "assistant" : "user");

const toFiniteNumber = (value, fallback = null) => {
  const parsed = parseFloat(value);
  return (Number.isFinite(parsed) ? parsed : fallback);
};

const toSafeString = (value, fallback = "") => {
  if (value === null || value === undefined) {
    return fallback;
  }
  return value.toString().trim();
};

const normalizeConversation = (messages) => {
  if (!Array.isArray(messages)) {
    return [];
  }

  return messages
    .filter((entry) => (
      entry &&
      typeof entry.content === "string" &&
      entry.content.trim().length > 0
    ))
    .slice(-16)
    .map((entry) => ({
      role: normalizeRole(entry.role),
      content: entry.content.trim().slice(0, 3000)
    }));
};

const normalizeBusinesses = (businesses) => {
  if (!Array.isArray(businesses)) {
    return [];
  }

  return businesses.slice(0, 120).map((entry) => ({
    id_event: toSafeString(entry && (entry.id_event || entry.idEvent || entry.event_id || entry.id)),
    id_business: toSafeString(entry && ((entry.business && entry.business.id_business) || entry.id_business)),
    title: toSafeString(entry && (entry.title || entry.name || entry.event_name)),
    type: toSafeString(entry && (entry.type || entry.category || entry.icon)),
    city: toSafeString(entry && (entry.city || (entry.business && entry.business.city))),
    date_start: toSafeString(entry && (entry.date_start || entry.start_date || entry.start)),
    position: (entry && entry.position ? entry.position : null)
  }));
};

const normalizeRecommendedIds = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (entry === null || entry === undefined) {
        return "";
      }
      if (typeof entry === "string" || typeof entry === "number") {
        return entry.toString().trim();
      }
      if (typeof entry === "object") {
        const id = (
          entry.id_event ||
          entry.idEvent ||
          entry.event_id ||
          entry.id ||
          entry.identity ||
          ""
        );
        return id.toString().trim();
      }
      return "";
    })
    .filter((entry) => entry.length > 0);
};

const resolveBackendEndpoint = () => {
  const configured = toSafeString(process.env.REACT_APP_MAP_CHAT_BACKEND_ENDPOINT);
  if (!configured) {
    return server.URL_SERVER_DJANGO + DEFAULT_CHAT_BACKEND_PATH;
  }

  if (/^https?:\/\//i.test(configured)) {
    return configured;
  }

  const path = (configured.startsWith("/") ? configured : ("/" + configured));
  return server.URL_SERVER_DJANGO + path;
};

const parseBackendErrorMessage = (payload, fallback) => {
  if (!payload || typeof payload !== "object") {
    return fallback;
  }

  const nested = (
    (payload.error && payload.error.message) ||
    payload.message ||
    payload.detail ||
    payload.error ||
    ""
  );
  const next = toSafeString(nested);
  return (next || fallback);
};

const normalizeBackendResponse = (payload) => {
  const ai = (payload && typeof payload.ai === "object" ? payload.ai : {});
  const replyText = toSafeString(
    ai.reply_text ||
    payload.reply_text ||
    payload.reply ||
    payload.text
  );
  if (!replyText) {
    throw new Error("Backend chat response is missing ai.reply_text.");
  }

  return {
    replyText: replyText,
    recommendedEventIds: normalizeRecommendedIds(
      ai.recommended_event_ids ||
      payload.recommended_event_ids
    ),
    eventsRaw: (Array.isArray(payload && payload.events_raw) ? payload.events_raw : []),
    areaUsed: (payload && payload.area_used ? payload.area_used : null),
    meta: (payload && payload.meta ? payload.meta : {})
  };
};

export const sendMapChatMessageViaBackend = async ({
  query,
  conversation,
  userLocation,
  radiusMeters,
  zoom,
  timeWindow,
  targetAreaText,
  limit = 8,
  loadedBusinesses,
  signal
}) => {
  const endpoint = resolveBackendEndpoint();
  const safeQuery = toSafeString(query);
  if (!safeQuery) {
    throw new Error("Cannot call backend chat without a user query.");
  }

  const headers = {
    "Content-Type": "application/json"
  };

  if (typeof window !== "undefined" && window.localStorage) {
    const token = toSafeString(window.localStorage.getItem("token"));
    if (token) {
      headers.Authorization = "Token " + token;
    }
  }

  const payload = {
    query: safeQuery,
    conversation: normalizeConversation(conversation),
    user_location: {
      lat: toFiniteNumber(userLocation && userLocation.latitude, 0),
      lon: toFiniteNumber(userLocation && userLocation.longitude, 0)
    },
    current_radius_m: toFiniteNumber(radiusMeters, 0),
    current_zoom: toFiniteNumber(zoom, 0),
    target_area_text: toSafeString(targetAreaText, ""),
    time_window: (timeWindow && typeof timeWindow === "object" ? timeWindow : null),
    limit: Math.max(1, Math.min(20, parseInt(limit, 10) || 8)),
    loaded_businesses: normalizeBusinesses(loadedBusinesses)
  };

  const timeoutController = new AbortController();
  const timeout = setTimeout(() => {
    timeoutController.abort();
  }, DEFAULT_TIMEOUT_MS);

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(payload),
      signal: (signal || timeoutController.signal)
    });

    let body = null;
    try {
      body = await response.json();
    } catch (error) {
      body = null;
    }

    if (!response.ok) {
      const message = parseBackendErrorMessage(
        body,
        "Backend chat request failed (" + response.status + ")."
      );
      throw new Error(message);
    }

    return normalizeBackendResponse(body);
  } catch (error) {
    if (error && error.name === "AbortError") {
      throw new Error("Backend chat request timed out.");
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
};
